import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { Card, FormRow, grid2, ONB_STATUS, PAY_STATUS, Pill } from "../../components/onboarding/adminUi";
import { LinkPanel } from "../../components/onboarding/LinkPanel";
import { AnswersView, HealthView } from "../../components/onboarding/AnswersView";
import { btn, fmtDate } from "../../components/leads/PriorityBadge";
import { ConvertToClient } from "../../components/onboarding/ConvertToClient";

const KEYS = ["player_name", "email", "phone", "plan_key", "duration_months", "payment_date", "payment_status", "activation_date", "end_date", "status", "notes", "welcome_message"];

const DataForm = ({ o, plans, durations, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => setF(Object.fromEntries(KEYS.map((k) => [k, o[k] ?? ""]))), [o]);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const save = async () => {
    const body = {};
    KEYS.forEach((k) => { const v = f[k] === "" ? null : f[k]; if (v !== (o[k] ?? null)) body[k] = v; });
    if (!Object.keys(body).length) return toast("Sin cambios");
    try { await api.patch(`/admin/onboardings/${o.id}`, body); toast.success("Guardado"); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const inp = (k, type = "text", tid) => <input className="qv-input" type={type} value={f[k] ?? ""} onChange={(e) => set({ [k]: e.target.value })} data-testid={tid || `onb-edit-${k}`} />;
  return (<>
    <Card title="Datos de la incorporación" testid="onb-data-card" right={<button style={btn("#dc2626")} onClick={save} data-testid="onb-save"><Save size={14} /> Guardar</button>}>
      <div style={grid2}>
        <FormRow label="Estado de la incorporación"><select className="qv-input" value={f.status || ""} onChange={(e) => set({ status: e.target.value })} data-testid="onb-edit-status">
          {Object.entries(ONB_STATUS).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}</select></FormRow>
        <FormRow label="Nombre del futbolista">{inp("player_name")}</FormRow>
        <FormRow label="Email">{o.user_id ? <input className="qv-input" value={f.email || ""} readOnly style={{ opacity: 0.6 }} title="Bloqueado: hay una cuenta vinculada" data-testid="onb-edit-email" /> : inp("email", "email")}</FormRow>
        <FormRow label="Teléfono">{inp("phone")}</FormRow>
        <FormRow label="Plan contratado"><select className="qv-input" value={f.plan_key || ""} onChange={(e) => set({ plan_key: e.target.value })} data-testid="onb-edit-plan_key">
          {plans.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}</select></FormRow>
        <FormRow label="Duración"><select className="qv-input" value={f.duration_months || ""} onChange={(e) => set({ duration_months: Number(e.target.value) })} data-testid="onb-edit-duration_months">
          {durations.map((m) => <option key={m} value={m}>{m} {m === 1 ? "mes" : "meses"}</option>)}</select></FormRow>
        <FormRow label="Fecha de pago">{inp("payment_date", "date")}</FormRow>
        <FormRow label="Estado del pago"><select className="qv-input" value={f.payment_status || ""} onChange={(e) => set({ payment_status: e.target.value })} data-testid="onb-edit-payment_status">
          {Object.entries(PAY_STATUS).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}</select></FormRow>
        <FormRow label="Fecha de activación">{inp("activation_date", "date")}</FormRow>
        <FormRow label="Finalización prevista">{inp("end_date", "date")}</FormRow>
      </div>
      <div style={{ marginTop: 14 }}><FormRow label="Notas internas"><textarea className="qv-input" rows={2} style={{ minHeight: 70, height: "auto" }} value={f.notes || ""} onChange={(e) => set({ notes: e.target.value })} data-testid="onb-edit-notes" /></FormRow></div>
      <div style={{ fontSize: 12, color: "#737373", marginTop: 10 }}>Si cambias activación o duración sin tocar la finalización, se recalcula sola.</div>
    </Card>
    <Card title="Mensaje de bienvenida" testid="onb-welcome-card" right={<button style={btn("transparent")} onClick={save} data-testid="onb-welcome-save"><Save size={14} /> Guardar</button>}>
      <textarea className="qv-input" rows={7} style={{ minHeight: 180, height: "auto", resize: "vertical" }} value={f.welcome_message || ""} onChange={(e) => set({ welcome_message: e.target.value })} data-testid="onb-edit-welcome_message" />
      <div style={{ fontSize: 12, color: "#737373", marginTop: 6 }}>{"{nombre}"} se sustituye por el nombre del futbolista.</div>
    </Card>
  </>);
};

export default function AdminOnboardingDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [cat, setCat] = useState({ plans: [], durations_months: [] });
  const load = useCallback(() => api.get(`/admin/onboardings/${id}`).then((r) => setO(r.data)).catch((e) => setErr(formatErr(e))), [id]);
  useEffect(() => { load(); api.get("/admin/client-management/plans").then((r) => setCat(r.data)).catch(() => {}); }, [load]);

  return (
    <AppShell>
      <Link to="/app/admin/incorporaciones" style={{ ...btn("transparent"), marginBottom: 18 }} data-testid="onb-back"><ArrowLeft size={14} /> Incorporaciones</Link>
      {err && <div style={{ color: "#f87171" }} data-testid="onb-error">{err}</div>}
      {!o && !err && <div style={{ color: "#a3a3a3" }}>Cargando…</div>}
      {o && (<div data-testid="onb-detail">
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
          <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2.2rem", letterSpacing: "0.04em", margin: 0 }} data-testid="onb-name-title">{o.player_name}</h1>
          <Pill map={ONB_STATUS} k={o.status} testid="onb-detail-status" />
          <Pill map={PAY_STATUS} k={o.payment_status} testid="onb-detail-payment" />
        </div>
        <div style={{ fontSize: 13, color: "#a3a3a3", marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }} data-testid="onb-links">
          <span>{o.plan_name} · {o.duration_months} meses · Creada {fmtDate(o.created_at)}{o.submitted_at && ` · Enviado ${fmtDate(o.submitted_at)}`}</span>
          {o.lead && <Link to={`/app/admin/leads/${o.lead.id}`} style={{ color: "#fff" }} data-testid="onb-linked-lead">Lead vinculado →</Link>}
          {o.user && (o.user.role === "client"
            ? <Link to={`/app/admin/clients/${o.user.id}`} style={{ color: "#fff" }} data-testid="onb-linked-client">Cliente vinculado →</Link>
            : <span data-testid="onb-linked-account">Cuenta creada ({o.user.email}) · sin acceso a planificación</span>)}
        </div>
        <ConvertToClient o={o} onDone={load} />
        <LinkPanel o={o} onChange={(d) => (d ? setO({ ...o, ...d }) : load())} />
        <DataForm o={o} plans={cat.plans} durations={cat.durations_months} onSaved={load} />
        <HealthView o={o} />
        <AnswersView o={o} />
      </div>)}
    </AppShell>
  );
}
